import React, { type ReactElement, useCallback, useEffect, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { isEqual } from 'lodash';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import AppliedFilters from 'atom-components/applied-filters-lite';
import Grid from 'atom-components/grid';
import Loading from 'atom-components/loading';
import MarkIdFilter, { MarkIdAppliedFilter } from 'pages/marking/components/filters/mark-id-filter';
import MarkerFilter, { MarkerAppliedFilter } from 'pages/marking/components/filters/marker-filter';
import ModeFilter, { ModeAppliedFilter } from 'pages/marking/components/filters/mode-filter';
import NSNFilter, { NSNAppliedFilter } from 'pages/marking/components/filters/nsn-filter';
import ResponseIdFilter, {
  ResponseIdAppliedFilter
} from 'pages/marking/components/filters/response-id-filter';
import TestFilter, { TestAppliedFilter } from 'pages/marking/components/filters/test-filter';
import {
  type FilterFormValues,
  filterFormDefaultValues,
  filterFormSchema
} from 'pages/marking/subpages/marker-dashboard-tab/filter-form/filter-form.schema';
import AllocatedToMeOnlyFilter, {
  AllocatedToMeOnlyAppliedFilter
} from './allocated-to-me-only-filter';
import CommentFilter, { CommentAppliedFilter } from './comment-filter';
import MarkingStartedFilter, { MarkingStartedAppliedFilter } from './marking-started-filter';
import ReviewFilter, { ReviewAppliedFilter } from './review-filter';
import StatusFilter, { StatusAppliedFilter } from './status-filter';

type Props = {
  defaultValuesFromURLParams: FilterFormValues;
  onSubmit: SubmitHandler<FilterFormValues>;
  onReset: () => void;
};

export default function FilterForm({
  defaultValuesFromURLParams,
  onSubmit,
  onReset
}: Props): ReactElement {
  const firstRenderRef = useRef(false);

  const methods = useForm({
    mode: 'onTouched',
    resolver: zodResolver(filterFormSchema),
    defaultValues: filterFormDefaultValues
  });
  const { handleSubmit, reset, watch } = methods;

  const triggerFormSubmission = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const triggerFormReset = useCallback(() => {
    reset(filterFormDefaultValues);
    onReset();
  }, [onReset, reset]);

  useEffect(() => {
    if (!firstRenderRef.current) {
      reset(defaultValuesFromURLParams);
      firstRenderRef.current = true;
    }
  }, [firstRenderRef, reset, defaultValuesFromURLParams]);

  // Ensure that URL params have loaded before populating comboboxes with initial selected items:
  if (!firstRenderRef.current) {
    return <Loading overlay={false} />;
  }

  const watchedAllFields = watch();

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid>
          <Grid.Row>
            <Grid.Column>
              <NSNFilter triggerFormSubmission={triggerFormSubmission} />
            </Grid.Column>
            <Grid.Column>
              <ResponseIdFilter triggerFormSubmission={triggerFormSubmission} />
            </Grid.Column>
            <Grid.Column>
              <MarkIdFilter triggerFormSubmission={triggerFormSubmission} />
            </Grid.Column>
            <Grid.Column>
              <TestFilter triggerFormSubmission={triggerFormSubmission} />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <ModeFilter triggerFormSubmission={triggerFormSubmission} />
            </Grid.Column>
            <Grid.Column>
              <StatusFilter triggerFormSubmission={triggerFormSubmission} />
            </Grid.Column>
            <Grid.Column>
              <MarkerFilter triggerFormSubmission={triggerFormSubmission} warnIfNotCurrentUser />
            </Grid.Column>
            <Grid.Column>
              <ReviewFilter triggerFormSubmission={triggerFormSubmission} />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <CommentFilter triggerFormSubmission={triggerFormSubmission} />
            </Grid.Column>
            <Grid.Column>
              <MarkingStartedFilter triggerFormSubmission={triggerFormSubmission} />
            </Grid.Column>
            <Grid.Column>
              <AllocatedToMeOnlyFilter triggerFormSubmission={triggerFormSubmission} />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column colSpan={12}>
              <AppliedFilters
                canClear={!isEqual(watchedAllFields, filterFormDefaultValues)}
                onClear={triggerFormReset}
              >
                <NSNAppliedFilter triggerFormSubmission={triggerFormSubmission} />
                <ResponseIdAppliedFilter triggerFormSubmission={triggerFormSubmission} />
                <MarkIdAppliedFilter triggerFormSubmission={triggerFormSubmission} />
                <TestAppliedFilter triggerFormSubmission={triggerFormSubmission} />
                <ModeAppliedFilter triggerFormSubmission={triggerFormSubmission} />
                <StatusAppliedFilter triggerFormSubmission={triggerFormSubmission} />
                <MarkerAppliedFilter
                  triggerFormSubmission={triggerFormSubmission}
                  warnIfNotCurrentUser
                />
                <ReviewAppliedFilter triggerFormSubmission={triggerFormSubmission} />
                <CommentAppliedFilter triggerFormSubmission={triggerFormSubmission} />
                <MarkingStartedAppliedFilter triggerFormSubmission={triggerFormSubmission} />
                <AllocatedToMeOnlyAppliedFilter triggerFormSubmission={triggerFormSubmission} />
              </AppliedFilters>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </form>
    </FormProvider>
  );
}
